// src/components/Hermes/VoiceVisualizer.tsx
//
// 语音音域动感图形组件
// 用于 Hermes 对话框的语音输入场景：
// 1. isActive=true 时获取麦克风，用 Web Audio API 的 AnalyserNode 读取频域数据
// 2. Canvas 绘制圆形声波（中心圆 + 多层环形波纹 + 频谱条），随声音脉动
// 3. 同时启动浏览器原生 SpeechRecognition（zh-CN）转文字，通过 onTranscript 回调
// 4. isActive=false 时停止录音、关闭 AudioContext、停止动画

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// 组件接口
// ============================================

interface VoiceVisualizerProps {
  /** 是否正在录音 */
  isActive: boolean;
  /** 语音转文字回调（实时返回当前识别文本，含中间结果） */
  onTranscript: (text: string) => void;
  /** 错误回调（麦克风权限被拒 / 浏览器不支持等） */
  onError?: (err: string) => void;
}

// ============================================
// SpeechRecognition 最小类型声明
// 浏览器原生 Web Speech API，TS 无内置类型，这里做最小化声明
// ============================================

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

/** 兼容获取 SpeechRecognition 构造器（标准 / webkit 前缀） */
function getSpeechRecognition(): SpeechRecognitionLike | null {
  const w = window as any;
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!Ctor) return null;
  return new Ctor() as SpeechRecognitionLike;
}

// ============================================
// 组件
// ============================================

export default function VoiceVisualizer({
  isActive,
  onTranscript,
  onError,
}: VoiceVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // 音频相关引用
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);

  // 语音识别引用
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // 已确认的最终识别文本（用于拼接中间结果，避免重复）
  const finalTranscriptRef = useRef<string>('');

  // 动画相位（驱动环形波纹扩散）
  const phaseRef = useRef<number>(0);

  // Canvas 的 CSS 尺寸（DPR 缩放后用于绘制坐标系）
  const sizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  // 记录最新的回调，避免 useEffect 闭包陈旧
  const cbRef = useRef({ onTranscript, onError });
  cbRef.current = { onTranscript, onError };

  // ----------------------------------------
  // 录音主流程：isActive 切换时启动 / 停止
  // ----------------------------------------
  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;
    finalTranscriptRef.current = '';

    // -------- 1. 启动麦克风 + 音频分析 --------
    const startAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // 异步期间组件可能已卸载或停止，需立即释放
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        mediaStreamRef.current = stream;

        const AudioCtor = (window.AudioContext ||
          (window as any).webkitAudioContext) as typeof AudioContext;
        const audioCtx = new AudioCtor();
        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }
        audioCtxRef.current = audioCtx;

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512; // 256 个频率桶
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        const source = audioCtx.createMediaStreamSource(stream);
        sourceRef.current = source;
        source.connect(analyser);

        startDraw();
      } catch (err: any) {
        console.error('[VoiceVisualizer] 麦克风获取失败:', err);
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          cbRef.current.onError?.('麦克风权限被拒绝，请在浏览器设置中允许使用麦克风');
        } else {
          cbRef.current.onError?.(`无法访问麦克风: ${err?.message || err}`);
        }
      }
    };

    // -------- 2. Canvas 绘制循环 --------
    const startDraw = () => {
      const canvas = canvasRef.current;
      const analyser = analyserRef.current;
      if (!canvas || !analyser) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const bins = analyser.frequencyBinCount; // 256
      const freqData = new Uint8Array(bins);

      const draw = () => {
        rafRef.current = requestAnimationFrame(draw);
        const analyserNow = analyserRef.current;
        const ctxNow = canvas.getContext('2d');
        if (!analyserNow || !ctxNow) return;

        const { w, h } = sizeRef.current;
        if (w === 0 || h === 0) return; // 尺寸尚未就绪

        analyserNow.getByteFrequencyData(freqData);

        const cx = w / 2;
        const cy = h / 2;

        // 计算音量（取低中频段均值，更贴近人声感知）
        const usable = Math.min(bins, 64);
        let sum = 0;
        for (let i = 0; i < usable; i++) sum += freqData[i];
        const avg = sum / usable; // 0-255
        const volume = Math.min(1, avg / 110); // 归一化 0-1

        // 半透明清屏，制造拖尾余晖
        ctxNow.fillStyle = 'rgba(11, 18, 32, 0.25)';
        ctxNow.fillRect(0, 0, w, h);

        phaseRef.current += 0.03;
        const phase = phaseRef.current;

        const minSide = Math.min(w, h);
        const baseRadius = minSide * 0.12; // 中心圆基础半径
        const centerRadius = Math.max(2, baseRadius + volume * baseRadius * 1.2);

        // ---- 多层环形波纹（随音量向外扩散） ----
        const ringCount = 4;
        for (let r = 0; r < ringCount; r++) {
          const ringRadius = Math.max(
            1,
            centerRadius + 10 + r * 14 + Math.sin(phase + r) * 6 + volume * 30 * (r + 1) * 0.4
          );
          const alpha = Math.max(0, 0.5 - r * 0.1 - (1 - volume) * 0.2);
          // 蓝(#38BDF8≈hsl199) → 绿(#34D399≈hsl160) 渐变
          const hue = 199 - r * 10;
          ctxNow.beginPath();
          ctxNow.arc(cx, cy, ringRadius, 0, Math.PI * 2);
          ctxNow.strokeStyle = `hsla(${hue}, 85%, 58%, ${alpha})`;
          ctxNow.lineWidth = 2;
          ctxNow.stroke();
        }

        // ---- 频谱条（环绕中心圆辐射） ----
        const barCount = 64;
        for (let i = 0; i < barCount; i++) {
          const v = freqData[Math.floor((i / barCount) * usable)] / 255;
          const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2;
          const inner = centerRadius + 6;
          const outer = inner + 4 + v * (minSide * 0.22);
          const x1 = cx + Math.cos(angle) * inner;
          const y1 = cy + Math.sin(angle) * inner;
          const x2 = cx + Math.cos(angle) * outer;
          const y2 = cy + Math.sin(angle) * outer;
          // 蓝 #38BDF8(56,189,248) → 绿 #34D399(52,211,153) 插值
          const t = i / barCount;
          const rr = Math.round(56 + (52 - 56) * t);
          const gg = Math.round(189 + (211 - 189) * t);
          const bb = Math.round(248 + (153 - 248) * t);
          ctxNow.strokeStyle = `rgba(${rr}, ${gg}, ${bb}, ${0.35 + v * 0.6})`;
          ctxNow.lineWidth = 2;
          ctxNow.beginPath();
          ctxNow.moveTo(x1, y1);
          ctxNow.lineTo(x2, y2);
          ctxNow.stroke();
        }

        // ---- 中心圆（辉光 + 径向渐变） ----
        ctxNow.save();
        ctxNow.shadowColor = '#38BDF8';
        ctxNow.shadowBlur = 20 + volume * 30;
        const grad = ctxNow.createRadialGradient(cx, cy, 0, cx, cy, centerRadius);
        grad.addColorStop(0, 'rgba(56, 189, 248, 0.9)'); // #38BDF8
        grad.addColorStop(0.6, 'rgba(52, 211, 153, 0.6)'); // #34D399
        grad.addColorStop(1, 'rgba(52, 211, 153, 0.05)');
        ctxNow.fillStyle = grad;
        ctxNow.beginPath();
        ctxNow.arc(cx, cy, centerRadius, 0, Math.PI * 2);
        ctxNow.fill();
        ctxNow.restore();
      };

      draw();
    };

    // -------- 3. 启动语音识别 --------
    const startRecognition = () => {
      const recognition = getSpeechRecognition();
      if (!recognition) {
        cbRef.current.onError?.(
          '当前浏览器不支持语音识别（SpeechRecognition），请使用 Chrome 内核浏览器'
        );
        return;
      }
      recognition.lang = 'zh-CN';
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onresult = (event: any) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const res = event.results[i];
          if (res.isFinal) {
            finalTranscriptRef.current += res[0].transcript;
          } else {
            interim += res[0].transcript;
          }
        }
        const full = (finalTranscriptRef.current + interim).trim();
        if (full) cbRef.current.onTranscript(full);
      };

      recognition.onerror = (event: any) => {
        console.error('[VoiceVisualizer] 语音识别错误:', event?.error);
        if (event?.error === 'not-allowed') {
          cbRef.current.onError?.('语音识别权限被拒绝');
        } else if (event?.error === 'no-speech') {
          // 无语音输入，忽略
        } else {
          cbRef.current.onError?.(`语音识别错误: ${event?.error || 'unknown'}`);
        }
      };

      // 识别自动结束时（如长时间静音），若仍在录音则重启
      recognition.onend = () => {
        if (!cancelled) {
          try {
            recognition.start();
          } catch {
            /* 忽略重复启动 */
          }
        }
      };

      try {
        recognition.start();
        recognitionRef.current = recognition;
      } catch (err) {
        console.error('[VoiceVisualizer] 语音识别启动失败:', err);
      }
    };

    startAudio();
    startRecognition();

    // -------- 清理：停止录音、关闭 AudioContext、停止动画 --------
    return () => {
      cancelled = true;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        try {
          recognitionRef.current.abort();
        } catch {
          /* */
        }
        recognitionRef.current = null;
      }
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch {
          /* */
        }
        sourceRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch {
          /* */
        }
        audioCtxRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [isActive]);

  // ----------------------------------------
  // Canvas 尺寸自适应（处理 DPR，保证清晰）
  // ----------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = wrap.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      sizeRef.current = { w, h };
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          ref={wrapRef}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 120, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="relative w-full overflow-hidden bg-[#0B1220]/80"
        >
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
